/**
 * Conversation Types
 * TypeScript types for chat-based conversation feature
 */

// Message types (discriminated union)
export type MessageType = "text" | "voice";

export type MessageRole = "user" | "assistant";

export type MessageStatus = "queued" | "sending" | "streaming" | "analyzing" | "complete" | "error";

// Error info for failed messages
export interface ErrorInfo {
  type: "network" | "timeout" | "server" | "auth" | "validation" | "business" | "unknown";
  /** Machine-readable error code from backend */
  code?: string;
  /** User-friendly message to display */
  message: string;
  /** Whether the operation can be retried */
  retryable: boolean;
  /** Request reference ID for support (only for unexpected errors) */
  referenceId?: string;
  /** Field-level validation errors */
  fieldErrors?: Record<string, string[]>;
}

// Analysis fields that can be added to any message
export interface AnalysisFields {
  analysis?: ConversationAnalysis;
  isAnalysisExpanded?: boolean;
  errorInfo?: ErrorInfo;
  /** Set when analysis specifically failed (message was delivered fine, status stays "complete") */
  analysisError?: ErrorInfo;
  /** True while analysis is running in the background (shows "Analyzing..." pending badge) */
  analysisPending?: boolean;
}

// Base message fields
export interface BaseMessageFields {
  id: string;
  userId: string;
  sessionId: string;
  createdAt: Date;
  status: MessageStatus;
  role?: MessageRole; // 'user' | 'assistant', defaults to 'user'
}

// Chat Flow DSL directives (parsed from trailing JSON in AI responses)
export interface ChatDirectives {
  question?: {
    text: string;
    options: string[];
  };
  pronunciation_assessment?: {
    reference_text?: string;
  };
  /**
   * Narrator-forward roleplay, PATTERN A — the learner initiates. The scene
   * prose arrives as the message `text` (streamed); this carries the forward
   * `cue`. No secondary character. Present when the agent called `narrate`.
   * Fields optional here for tolerance of older messages.
   */
  narrate?: {
    cue?: string;
  };
  /**
   * Narrator-forward roleplay, PATTERN B — a secondary character drives and
   * the learner replies. The scene prose arrives as the message `text`
   * (streamed); this carries the speaker's line and the learner's reply
   * `cue`. Present when the agent called `narrate_reply`.
   */
  narrate_reply?: {
    speaker_name?: string;
    speaker_line?: string;
    cue?: string;
  };
  /**
   * Narrator-forward roleplay, PATTERN C — a secondary character reacts to
   * what the learner just said. The scene prose arrives as the message `text`
   * (streamed); this carries the character's reaction (rendered at the top of
   * the turn) and the next forward `cue`. Present when the agent called
   * `narrate_react`.
   */
  narrate_react?: {
    speaker_name?: string;
    reaction_line?: string;
    cue?: string;
  };
}

/**
 * Tool names that carry a UI directive. Runtime mirror of the backend-generated
 * `DIRECTIVE_TOOL_NAMES` (backend/src/generated/chat-flows/tools.ts) and of the
 * keys of {@link ChatDirectives}.
 *
 * Used to rebuild `ChatDirectives` from the raw `toolCalls` persisted on an
 * assistant message when a session is loaded from history: the GET-messages
 * contract returns raw `toolCalls`, not parsed directives, so the live-socket
 * directive (e.g. `pronunciation_assessment.reference_text`) would otherwise be
 * lost on reload — collapsing a scripted pronunciation drill to unscripted STT.
 *
 * `satisfies` guarantees every entry is a real `ChatDirectives` key.
 */
export const DIRECTIVE_TOOL_NAMES = [
  "question",
  "pronunciation_assessment",
  "narrate",
  "narrate_reply",
  "narrate_react",
] as const satisfies ReadonlyArray<keyof ChatDirectives>;

export type DirectiveToolName = (typeof DIRECTIVE_TOOL_NAMES)[number];

// Text message (analyzed)
export interface TextMessage extends BaseMessageFields, AnalysisFields {
  type: "text";
  text: string;
  /** Parsed directives from Chat Flow DSL (AI assistant messages only) */
  directives?: ChatDirectives | null;
}

// Voice message (analyzed)
export interface VoiceMessage extends BaseMessageFields, AnalysisFields {
  type: "voice";
  transcription: string;
  pronunciationResult: PronunciationResult;
}

// Union type for all messages
export type Message = TextMessage | VoiceMessage;

// Alias for compatibility (AnalyzedMessage = Message now since analysis fields are in base)
export type AnalyzedMessage = Message;

// Analysis types
export interface GrammarError {
  original: string;
  correction: string;
  explanation: string;
  type: string; // 'past_tense', 'article', 'preposition', etc.
}

export interface NativeExpression {
  nativeVersion: string;
  explanation: string;
  differences?: string[];
}

export interface LessonReference {
  errorType: string;
  lessonId: string;
  slug: string;
  title: string;
}

/**
 * Mirrors the `AnalysisResult` contract carried by `analysis_complete`
 * (specs/api/components/schemas.yaml). `overallLevel` is deliberately absent:
 * the model produces it, but it stays server-side to feed the learner profile.
 */
export interface ConversationAnalysis {
  grammarErrors: GrammarError[];
  nativeExpression?: NativeExpression | null;
  encouragement?: string;
  lessonReferences?: LessonReference[];
}

// Pronunciation assessment types

/** Assessment profile — tells the backend what scenario this was */
export type AssessmentProfile = 'word' | 'phrase' | 'sentence' | 'free';

export interface PronunciationResult {
  accuracyScore: number;
  pronunciationScore: number;
  fluencyScore: number;
  completenessScore: number;
  prosodyScore?: number;
  words: PronunciationWord[];
  profile?: AssessmentProfile;
  /** Raw Azure response — forwarded to backend as `pronunciationRaw` for Phase 0
   *  storage. Bare Azure JSON for single-segment takes; multi-segment takes carry
   *  a `{"segments": [...]}` envelope (SPEECH-MOD-004). Opaque to the mobile. */
  rawJson?: string;
}

export interface PronunciationWord {
  word: string;
  accuracyScore: number;
  errorType?: string;
  phonemes?: PronunciationPhonemeData[];
  syllables?: PronunciationSyllableData[];       // en-US only
  prosodyFeedback?: WordProsodyFeedbackData;      // en-US only
}

export interface PronunciationPhonemeData {
  phoneme: string;
  accuracyScore: number;
  nbestPhonemes?: Array<{ phoneme: string; score: number }>;
}

export interface PronunciationSyllableData {
  syllable: string;
  accuracyScore: number;
}

export interface WordProsodyFeedbackData {
  unexpectedBreakConfidence?: number;
  missingBreakConfidence?: number;
  monotoneConfidence?: number;
}

// Conversation session
export interface ConversationSession {
  id: string;
  userId: string;
  messages: AnalyzedMessage[];
  createdAt: Date;
  updatedAt: Date;
}

// ── Server-side API types ────────────────────────────────────────────
// Request/response shapes are defined in generated/http-contracts.ts
// (generated from the AsyncAPI spec). Import from there instead of
// defining inline types.
